import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, MarkerF, useLoadScript, Autocomplete } from "@react-google-maps/api";
import { App, Modal } from "antd";
import { GOOGLE_MAP_LIBRARIES } from "../../../constants/googleMaps";

const containerStyle = { width: "100%", height: 320 };
// const libraries = ["places"];
const libraries = GOOGLE_MAP_LIBRARIES;

const ClientLocationMap = ({ value, onChange, authInfo }) => {
  const { message } = App.useApp();
  const [marker, setMarker] = useState(value ?? null);
  const autocompleteRef = useRef(null);
  const [map, setMap] = useState(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries,
  });

  useEffect(() => {
    setMarker(value ?? null);
  }, [value?.latitude, value?.longitude]);

  const center = useMemo(() => {
    if (marker?.latitude && marker?.longitude)
      return { lat: Number(marker.latitude), lng: Number(marker.longitude) };
    return { lat: 25.19659, lng: 55.3050064 };
  }, [marker]);

  const onMapClick = useCallback((e) => {
    Modal.confirm({
      title: "Set Client Location?",
      content: "Do you want to set this as the client's office location?",
      onOk: () => {
        const loc = { latitude: e.latLng.lat(), longitude: e.latLng.lng() };
        setMarker(loc);
        onChange?.(loc);
      },
    });
  }, []);

  const onPlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry || !place.geometry.location) {
      message.warning("No details available for selected location.");
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const newMarker = { latitude: lat, longitude: lng };

    if (map) {
      map.panTo({ lat, lng });
      map.setZoom(15);
    }

    Modal.confirm({
      title: "Use this location?",
      content: place.formatted_address,
      onOk: () => {
        setMarker(newMarker);
        onChange?.(newMarker);
      },
    });
  };

  if (loadError) return <div>Map failed to load.</div>;
  if (!isLoaded) return <div>Loading map…</div>;

  return (
    <div  data-testid="client-location-map" style={{ position: "relative" }}>
      {/* Map Container */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={marker?.latitude ? 14 : 5}
        center={center}
        onClick={onMapClick}
        onLoad={setMap}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {marker?.latitude && (
          <MarkerF
            position={{ lat: Number(marker.latitude), lng: Number(marker.longitude) }}
            onClick={() =>
              Modal.confirm({
                title: "Remove Location?",
                content: "Are you sure you want to remove this location?",
                okText: "Remove",
                okButtonProps: { danger: true },
                onOk: () => {
                  setMarker(null);
                  onChange?.(null);
                },
              })
            }
          />
        )}
      </GoogleMap>

      {/* Overlay Search Bar */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          width: "90%",
          maxWidth: 320,
          background: "white",
          borderRadius: 8,
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}
      >
        <Autocomplete
          onLoad={(a) => (autocompleteRef.current = a)}
          onPlaceChanged={onPlaceChanged}
        >
          <input
            type="text"
            placeholder="Search client location..."
            style={{
              width: "100%",
              height: 38,
              padding: "0 10px",
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              outline: "none",
            }}
          />
        </Autocomplete>
      </div>
    </div>
  );
};

export default ClientLocationMap;
